import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { motion, AnimatePresence } from 'framer-motion'
import { User, LogOut, Settings, Shield, ChevronDown } from 'lucide-react'

export function UserMenu() {
  const { user, profile, signOut } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Extract name from email (vorname.nachname@domain.de)
  const getNameFromEmail = (email: string | undefined) => {
    if (!email) return 'Benutzer'
    const namePart = email.split('@')[0]
    const parts = namePart.split('.')
    if (parts.length >= 2) {
      // Capitalize first letter of each part
      const firstName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
      const lastName = parts[parts.length - 1].charAt(0).toUpperCase() + parts[parts.length - 1].slice(1)
      return `${firstName} ${lastName}`
    }
    return namePart
  }

  // Don't use full_name if it's "JWT User"
  const displayName = (profile?.full_name && profile.full_name !== 'JWT User') 
    ? profile.full_name 
    : getNameFromEmail(user?.email)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  const handleManagementClick = () => {
    navigate('/management')
    setIsOpen(false)
  }


  const isManagement = profile?.role === 'management' || profile?.role === 'admin'

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-white/10 transition-smooth"
      >
        <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center">
          <User className="w-4 h-4 text-white" />
        </div>
        <span className="hidden md:block text-sm font-medium">
          {displayName}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 mt-2 w-64 glass rounded-xl overflow-hidden z-50"
          >
            <div className="p-4 border-b border-white/10">
              <p className="font-medium">{displayName}</p>
              <p className="text-sm text-gray-400">{user?.email}</p>
              {profile?.role && (
                <span className="inline-flex items-center mt-2 px-2 py-1 rounded-full text-xs bg-primary-600/20 text-primary-400">
                  <Shield className="w-3 h-3 mr-1" />
                  {profile.role === 'admin' ? 'Administrator' : 
                   profile.role === 'management' ? 'Management' : 'Franchise-Nehmer'}
                </span>
              )}
            </div>

            <div className="p-2">
              {isManagement && (
                <button
                  onClick={handleManagementClick}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 transition-smooth flex items-center space-x-3"
                >
                  <Settings className="w-4 h-4 text-gray-400" />
                  <span>Management Portal</span>
                </button>
              )}

              <button
                onClick={handleSignOut}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 transition-smooth flex items-center space-x-3 text-red-400"
              >
                <LogOut className="w-4 h-4" />
                <span>Abmelden</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}